import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setUnauthorizedHandler } from './api';
import { getToken, setToken } from './storage';
import { disablePush } from './push';
import { askLaunchPermissions } from './permissions';
import { stopTracking } from './tracking/task';

export type Role = 'admin' | 'intake' | 'call' | 'invoice' | 'invoice_admin' | 'prep' | 'ship' | 'driver' | 'coll' | 'finance' | 'can_cancel' | 'can_back';

/** Detailed permissions (App\Ops\StaffPermissions on the server, which checks them again). */
export type StaffPerms = {
  admin: boolean;
  flags: Record<string, boolean>;
  staff: Record<string, string[]>;
  cities: 'all' | number[];
  manages_staff: boolean;
};

export type StaffUser = {
  id: number;
  name: string;
  username: string | null;
  phone: string | null;
  role: string | null; // main job, e.g. "ops_driver"
  role_label: string | null;
  roles: Role[]; // every job the account holds (logistics = prep + ship + coll)
};

type Profile = { user: StaffUser; settings: { tracking_interval_sec: number }; permissions?: StaffPerms };

type AuthState = {
  ready: boolean;
  user: StaffUser | null;
  trackingInterval: number;
  signIn: (login: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  has: (...roles: Role[]) => boolean;
  /** A permission switch ("money_view", "tracking_live"…), or "staff" = manages some accounts. Admin: always. */
  can: (perm: string) => boolean;
  /** Reloads the profile (after an admin changed this account's permissions). */
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);

  const clear = useCallback(async () => {
    await stopTracking().catch(() => {});
    await setToken(null);
    setProfile(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clear();
    });
    (async () => {
      try {
        if (await getToken()) {
          const p = await api<Profile>('/ops/me');
          setProfile(p);
          // Notifications + location prompts (phones) / keep this browser registered (web).
          askLaunchPermissions(!!p.user?.roles?.includes('driver'));
        }
      } catch {
        // offline or signed out: show the login screen
      } finally {
        setReady(true);
      }
    })();
  }, [clear]);

  const signIn = useCallback(async (login: string, password: string) => {
    const data = await api<Profile & { token: string }>('/ops/login', { body: { login, password }, token: null });
    await setToken(data.token);
    setProfile({ user: data.user, settings: data.settings, permissions: data.permissions });
    askLaunchPermissions(!!data.user?.roles?.includes('driver'));
  }, []);

  const signOut = useCallback(async () => {
    await disablePush(); // this device stops receiving the account's notifications
    try {
      await api('/ops/logout', { method: 'POST' });
    } catch {
      // the token is dropped locally anyway
    }
    await clear();
  }, [clear]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user: profile?.user ?? null,
      trackingInterval: profile?.settings.tracking_interval_sec ?? 30,
      signIn,
      signOut,
      has: (...roles) => {
        const held = profile?.user.roles ?? [];
        return held.includes('admin') || roles.some((r) => held.includes(r));
      },
      can: (perm) => {
        const p = profile?.permissions;
        if (!p) return !!profile?.user.roles.includes('admin'); // older server: admin-only pages
        if (p.admin) return true;
        return perm === 'staff' ? p.manages_staff : !!p.flags[perm];
      },
      refresh: async () => {
        try {
          setProfile(await api<Profile>('/ops/me'));
        } catch {
          // keep the current profile
        }
      },
    }),
    [ready, profile, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
